import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchActiveRuns } from '../services/activeRunsService';
import { getDeviceIdForAppiumScripts } from '../utilities/getDevideIdForAppiumScripts';
import { useSelector } from 'react-redux';
import { selectSelectedDevice } from '../redux/app/platformSlice';

const ACTIVE_SCENARIO_STATUSES = ['running', 'queued'];

export const getScenarioRunKey = (flowId, scenarioId) => `${flowId}::${scenarioId}`;

export default function useActiveScenarioStatuses(options = {}) {
  const { enabled = true, intervalMs = 2000, requireDevice = false } = options;
  const selectedDevice = useSelector(selectSelectedDevice);
  const [runs, setRuns] = useState([]);

  const deviceId = useMemo(() => getDeviceIdForAppiumScripts(selectedDevice), [selectedDevice]);

  // When requireDevice is set (e.g. on /app-flows), don't poll until a device is
  // selected — there are no per-device statuses to show yet. Pages that need the
  // global live feed (e.g. /active-runs) leave this off and always poll.
  const active = enabled && (!requireDevice || !!selectedDevice);

  useEffect(() => {
    if (!active) {
      setRuns([]);
      return undefined;
    }

    let mounted = true;

    const loadActiveRuns = async () => {
      try {
        const res = await fetchActiveRuns(deviceId);
        if (mounted) setRuns(res?.runs || []);
      } catch (error) {
        console.error('Failed to load active scenario statuses', error);
      }
    };

    loadActiveRuns();
    const id = setInterval(loadActiveRuns, intervalMs);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [deviceId, active, intervalMs]);

  const scenarioStatusMap = useMemo(() => {
    const map = {};

    runs.forEach((run) => {
      if (ACTIVE_SCENARIO_STATUSES.includes(run.status) && run.flowId && run.scenarioId) {
        map[getScenarioRunKey(run.flowId, run.scenarioId)] = run.status;
      }
    });

    return map;
  }, [runs]);

  const getScenarioStatus = useCallback(
    (flowId, scenarioId) => scenarioStatusMap[getScenarioRunKey(flowId, scenarioId)] || null,
    [scenarioStatusMap],
  );

  return {
    deviceId,
    getScenarioStatus,
    runs,
    scenarioStatusMap,
    selectedDevice,
  };
}
